import { useCallback, useState } from "react";
import { useAuth } from "react-oidc-context";
import { getOidcConfig } from "@/config/auth";
import { authApi } from "@/lib/humhub-api/auth";
import type { User } from "@/lib/humhub-api/types";

export const useLogout = () => {
  const auth = useAuth();
  const [_userInfo, setUserInfo] = useState<User | null>(null);

  /**
   * グローバルログアウト（HumHubとOIDC両方からログアウト）
   * OIDCのトークンはバックエンドで無効化する
   */
  const logout = useCallback(async () => {
    try {
      // HumHubログアウト
      try {
        await authApi.oidcLogout();
      } catch (backendError) {
        console.warn("⚠️ Backend logout request failed:", backendError);
        // バックエンドのエラーでもフロントエンドのクリーンアップは継続
      }

      // メモリ上のOIDCローカル状態をクリア
      auth.removeUser();

      // stateのユーザー情報をクリア
      setUserInfo(null);

      // OIDC設定からend_session_endpointを取得
      const oidcConfig = getOidcConfig();
      const endSessionEndpoint = oidcConfig.metadata?.end_session_endpoint;

      if (endSessionEndpoint) {
        // end_session_endpointにクッキーを送信してリダイレクト
        // ID TokenはHttpOnlyクッキーなので、credentials: 'include'でクッキーを送信
        const logoutUrl = new URL(endSessionEndpoint);

        // post_logout_redirect_uriのみクエリパラメータとして設定
        if (oidcConfig.post_logout_redirect_uri) {
          logoutUrl.searchParams.append(
            "post_logout_redirect_uri",
            oidcConfig.post_logout_redirect_uri,
          );
        }

        // クッキーを含めてリダイレクト（ブラウザが自動的にクッキーを送信）
        window.location.href = logoutUrl.toString();
      } else {
        console.warn("⚠️ Could not find OIDC end session endpoint, falling back to default logout");
        auth.removeUser();
        auth.signoutRedirect();
      }
    } catch (error) {
      console.error("❌ Logout error:", error);

      // エラーが発生してもローカル状態はクリア
      auth.removeUser();
      setUserInfo(null);
      auth.signoutRedirect();
    }
  }, [auth]);

  return { logout };
};
